import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, feedbackAPI } from '../services/api';

const PortalContext = createContext(null);

export const usePortal = () => useContext(PortalContext);

const NOTIF_READ_KEY = 'portal_notification_read_ids';

export const PortalProvider = ({ children }) => {
  const [portalUser, setPortalUser] = useState(null);
  const [portalLoading, setPortalLoading] = useState(true);
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  const checkPortalAuth = useCallback(async () => {
    const token = localStorage.getItem('portalToken');
    const refreshToken = localStorage.getItem('portalRefreshToken');
    if (!token) {
      setPortalLoading(false);
      return;
    }

    // Check if access token is expired
    let tokenExpired = false;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.exp * 1000 < Date.now()) {
        tokenExpired = true;
      }
    } catch {
      tokenExpired = true;
    }

    if (tokenExpired && refreshToken) {
      // Try to refresh
      try {
        const resp = await authAPI.portalRefresh(refreshToken);
        if (resp.data?.access_token) {
          localStorage.setItem('portalToken', resp.data.access_token);
          if (resp.data?.refresh_token) {
            localStorage.setItem('portalRefreshToken', resp.data.refresh_token);
          }
          setPortalUser(resp.data.portal_user);
          setPortalLoading(false);
          return;
        }
      } catch {
        localStorage.removeItem('portalToken');
        localStorage.removeItem('portalRefreshToken');
        setPortalLoading(false);
        return;
      }
    } else if (tokenExpired) {
      localStorage.removeItem('portalToken');
      localStorage.removeItem('portalRefreshToken');
      setPortalLoading(false);
      return;
    }

    // Fetch current portal user.
    // Only clear the session on an actual auth failure (401/403); a transient
    // network error must NOT log the user out.
    try {
      const resp = await authAPI.portalMe();
      setPortalUser(resp.data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem('portalToken');
        localStorage.removeItem('portalRefreshToken');
        setPortalUser(null);
      }
      // Network errors (no response) leave the session intact
    }
    setPortalLoading(false);
  }, []);

  useEffect(() => {
    checkPortalAuth();
  }, [checkPortalAuth]);

  const portalSendOtp = async (email, name) => {
    try {
      const resp = await authAPI.portalSendOtp(email, name);
      return { success: true, message: resp.data?.message };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Could not send the verification code',
      };
    }
  };

  const portalVerifyOtp = async (email, otpCode) => {
    try {
      const resp = await authAPI.portalVerifyOtp(email, otpCode);
      return { success: true, message: resp.data?.message };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Could not verify the code',
      };
    }
  };

  const portalRegister = async (name, email, password, otpCode) => {
    try {
      const resp = await authAPI.portalRegister(name, email, password, otpCode);
      const { access_token, refresh_token, portal_user } = resp.data;
      if (access_token) { localStorage.setItem('portalToken', access_token); }
      if (refresh_token) {
        if (refresh_token) { localStorage.setItem('portalRefreshToken', refresh_token); }
      }
      setPortalUser(portal_user);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Registration failed',
      };
    }
  };

  const portalLogin = async (email, password) => {
    try {
      const resp = await authAPI.portalLogin(email, password);
      const { access_token, refresh_token, portal_user } = resp.data;
      if (access_token) { localStorage.setItem('portalToken', access_token); }
      if (refresh_token) {
        if (refresh_token) { localStorage.setItem('portalRefreshToken', refresh_token); }
      }
      setPortalUser(portal_user);
      return { success: true, portal_user };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed',
      };
    }
  };

  const portalGoogleSignIn = async (credential) => {
    try {
      const resp = await authAPI.portalGoogleSignIn(credential);
      const { access_token, refresh_token, portal_user } = resp.data;
      if (access_token) { localStorage.setItem('portalToken', access_token); }
      if (refresh_token) {
        if (refresh_token) { localStorage.setItem('portalRefreshToken', refresh_token); }
      }
      setPortalUser(portal_user);
      return { success: true, portal_user };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Google sign-in failed',
      };
    }
  };

  const portalMicrosoftSignIn = async (credential) => {
    try {
      const resp = await authAPI.portalMicrosoftSignIn(credential);
      const { access_token, refresh_token, portal_user } = resp.data;
      if (access_token) { localStorage.setItem('portalToken', access_token); }
      if (refresh_token) {
        if (refresh_token) { localStorage.setItem('portalRefreshToken', refresh_token); }
      }
      setPortalUser(portal_user);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Microsoft sign-in failed',
      };
    }
  };

  const portalAppleSignIn = async (credential) => {
    try {
      const resp = await authAPI.portalAppleSignIn(credential);
      const { access_token, refresh_token, portal_user } = resp.data;
      if (access_token) { localStorage.setItem('portalToken', access_token); }
      if (refresh_token) {
        if (refresh_token) { localStorage.setItem('portalRefreshToken', refresh_token); }
      }
      setPortalUser(portal_user);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Apple sign-in failed',
      };
    }
  };

  const portalLogout = async () => {
    // Revoke the refresh token server-side so it cannot be reused.
    // Without this, the 7-day refresh token remains valid even after
    // the user "logs out" — a stolen token survives logout.
    const refreshToken = localStorage.getItem('portalRefreshToken');
    try {
      if (refreshToken) {
        await feedbackAPI.portalLogout(refreshToken);
      }
    } catch {
      // Non-fatal — proceed with local cleanup regardless
    }
    localStorage.removeItem('portalToken');
    localStorage.removeItem('portalRefreshToken');
    setPortalUser(null);
    setUnreadNotifications(0);
  };

  // --- Notification unread count (shared between header and dashboard) ---
  const refreshUnreadCount = useCallback(async () => {
    if (!portalUser) return;
    try {
      const resp = await feedbackAPI.portalNotifications();
      const notifs = resp.data?.notifications || [];
      const readIds = new Set(JSON.parse(localStorage.getItem(NOTIF_READ_KEY) || '[]'));
      const unread = notifs.filter(n => !readIds.has(n.id)).length;
      setUnreadNotifications(unread);
    } catch { /* ignore */ }
  }, [portalUser]);

  // Fetch unread count on mount and poll every 60s
  useEffect(() => {
    if (!portalUser) return;
    refreshUnreadCount();
    const interval = setInterval(refreshUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [portalUser, refreshUnreadCount]);

  const markNotificationsRead = useCallback((ids) => {
    try {
      const existing = new Set(JSON.parse(localStorage.getItem(NOTIF_READ_KEY) || '[]'));
      ids.forEach(id => existing.add(id));
      localStorage.setItem(NOTIF_READ_KEY, JSON.stringify([...existing]));
    } catch { /* ignore */ }
    setUnreadNotifications(0);
  }, []);

  return (
    <PortalContext.Provider
      value={{
        portalUser,
        portalLoading,
        portalSendOtp,
        portalVerifyOtp,
        portalRegister,
        portalLogin,
        portalGoogleSignIn,
        portalMicrosoftSignIn,
        portalAppleSignIn,
        portalLogout,
        checkPortalAuth,
        unreadNotifications,
        refreshUnreadCount,
        markNotificationsRead,
      }}
    >
      {children}
    </PortalContext.Provider>
  );
};
